import { useEffect, useState, useCallback } from 'react';
import { getAPI } from '../services/api.service';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { setAllGroups } from '../ReduxToolkit/GroupReducers/GroupReducer';

export interface Group {
    id: string;
    title: string;
    bookName: string;
    date: string;
    description: string;
    isArchived: boolean;
    participantsMerged?: any[];
    participants?: any[];
}

export const useFetchGroups = (userId: string, role?: string) => {
    const [groups, setGroups] = useState<Group[]>([]);
    const [userSpecificGroups, setUserSpecificGroups] = useState<Group[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const dispatch = useDispatch();

    const fetchGroups = useCallback(async () => {
        // alert("fetchGroups called");
        setLoading(true);
        try {
            if(!userId || !role) {
                setError('User ID or role is missing');
                setLoading(false);
                return;
            }
            const response = await getAPI<{ data: Group[] }>('/groups/all', { userId, role });
            if (response.status === 200 && response.data && response.data.data) {
                setGroups(response.data.data);
                dispatch(setAllGroups(response?.data?.data as Group[])); // Dispatch to Redux store

            } else {
                setError('Failed to fetch groups');
            }
        } catch (err) {
            setError('An error occurred while fetching groups');
        } finally {
            setLoading(false);
        }
    }, [userId, role]);

    const fetchUserSpecificGroups = useCallback(async (fetchArchivedGrps=false) => {
        setLoading(true);
        try {
            // Get logged-in user id from localStorage (consistent with your pattern)
            if (!userId) {
                toast.error('User not found. Please login again.');
                setUserSpecificGroups([]);
                setLoading(false);
                return;
            }

            // call the API   you already created
            // endpoint: /groups/by-user?userId=...
            const res = await getAPI<{ data: any, message: string }>(`groups/by-user?userId=${userId}&fetchArchivedGrps=${fetchArchivedGrps}`);

            // Expecting res.data.data to be an array (based on your backend response shape)
            if (res?.status === 200) {
                const data = res.data?.data || [];
                setUserSpecificGroups(Array.isArray(data) ? data : []);
            } else {
                const msg = res?.data?.message || 'Unable to fetch groups';
                toast.error(msg);
                setUserSpecificGroups([]);
            }
        } catch (err: any) {
            console.error('fetch groups error', err);
            const message = err?.response?.data?.message || 'Failed to fetch groups';
            toast.error(message);
            setUserSpecificGroups([]);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    useEffect(() => {
        fetchUserSpecificGroups();
    }, [fetchUserSpecificGroups]);

    return { groups,loading, error, refetch: fetchGroups, fetchUserSpecificGroups, userSpecificGroups }; // Return refetch function
};
